/**
 * Listing Types for Royal Frontend
 * 
 * These types represent the listing data as used in the frontend,
 * derived from the Route Royal Listings API.
 */

import type { 
  ListingOnChannel, 
  Listing, 
  ListingType,
  ListingStatus,
} from '@/lib/api/clients/listings';

import type { 
  FeaturedPlacementSummary,
} from '@/lib/api/clients/featured-content';
import { DEFAULT_CURRENCY } from '@/lib/config/app.config';

/**
 * Simplified listing data for card display
 */
export interface ListingCardData {
  id: string;
  listingId: string;
  
  // Core property info
  property: {
    id: string;
    name: string;
    description: string | null;
    bedrooms: number | null;
    bathrooms: number | null;
    totalFloorArea: number | null;
    totalFloorAreaUnit: string | null;
    landArea: number | null;
    landAreaUnit: string | null;
    yearBuilt: number | null;
    condition: string | null;
    primaryImageUrl: string | null;
    currency: string;
  };
  
  // Location
  location: {
    name: string | null;
    city: string | null;
    district: string | null;
    neighbourhood: string | null;
    region: string | null;
    country: string | null;
    suburb: string | null;
    addressLine1: string | null;
    latitude: number | null;
    longitude: number | null;
  } | null;
  
  // Listing type and status
  listingType: ListingType;
  status: ListingStatus;
  displayPriceText: string | null;
  mainHeadline: string | null;
  
  // Pricing
  price: {
    amount: number;
    currency: string;
    priceType: string;
    frequency?: string | null;
    isTaxInclusive?: boolean | null;
  } | null;
  
  // For rentals
  rentalTerms: {
    minLeaseMonths: number | null;
    maxLeaseMonths: number | null;
    furnishedStatus: string | null;
    petPolicy: string | null;
    depositAmount: number | null;
    depositCurrency: string | null;
  } | null;
  
  // For auctions
  auctionDetails: {
    auctionDate: string | null;
    auctionType: string | null;
    guidePriceMin: number | null;
    guidePriceMax: number | null;
    currency: string | null;
  } | null;
  
  // Agent
  listingAgent: {
    id: string;
    name: string;
    email: string;
    avatar: string | null;
    jobTitle: string | null;
  } | null;
  
  // Dates
  listedDate: string | null;
  availableDate: string | null;
  
  // Property type
  propertyType: {
    id: string;
    name: string;
    icon: string | null;
  } | null;
  
  // Parent development (if this listing's property belongs to a development)
  development?: {
    id: string;
    name: string;
    slug?: string | null;
  } | null | undefined;
  
  // Featured content
  /** Whether this listing is currently featured */
  isFeatured?: boolean;
  
  /** Active featured placements for this listing */
  featuredPlacements?: FeaturedPlacementSummary[];
}

/**
 * Transform a ListingOnChannel from the API to ListingCardData for display
 */
export function transformListingToCardData(listingOnChannel: ListingOnChannel): ListingCardData | null {
  const listing = listingOnChannel.listing;
  if (!listing) return null;
  
  const property = listing.managedAsset?.property;
  if (!property) return null;
  
  const location = property.location;
  
  // Extract price based on listing type
  const price = extractPrimaryPrice(listing);
  
  // Extract rental terms if applicable
  const rentalTerms = listing.rentalDetails ? {
    minLeaseMonths: listing.rentalDetails.minLeaseTermMonths,
    maxLeaseMonths: listing.rentalDetails.maxLeaseTermMonths,
    furnishedStatus: listing.rentalDetails.furnishedStatus || null,
    petPolicy: listing.rentalDetails.petPolicy || null,
    depositAmount: listing.rentalDetails.depositPrice?.amount || null,
    depositCurrency: listing.rentalDetails.depositPrice?.currency || null,
  } : null;
  
  // Extract auction details if applicable
  const auctionDetails = listing.auctionDetails ? {
    auctionDate: listing.auctionDetails.auctionDate ?? null,
    auctionType: (listing.auctionDetails.auctionType as string) || null,
    guidePriceMin: listing.auctionDetails.guidePriceMin?.amount ?? null,
    guidePriceMax: listing.auctionDetails.guidePriceMax?.amount ?? null,
    currency: listing.auctionDetails.guidePriceMin?.currency || 
              listing.auctionDetails.guidePriceMax?.currency || null,
  } : null;
  
  // Extract listing agent
  const listingAgent = listing.listingAgent ? {
    id: listing.listingAgent.id,
    name: listing.listingAgent.displayName || 
          `${listing.listingAgent.individualProfile?.firstName || ''} ${listing.listingAgent.individualProfile?.lastName || ''}`.trim() ||
          listing.listingAgent.email,
    email: listing.listingAgent.email,
    avatar: listing.listingAgent.avatar || null,
    jobTitle: listing.listingAgent.individualProfile?.jobTitle || null,
  } : null;
  
  return {
    id: listingOnChannel.id,
    listingId: listing.id,
    
    property: {
      id: property.id,
      name: property.name,
      description: property.description ?? null,
      bedrooms: property.bedrooms ?? null,
      bathrooms: property.bathrooms ?? null,
      totalFloorArea: property.totalFloorArea ?? null,
      totalFloorAreaUnit: property.totalFloorAreaUnit ?? null,
      landArea: property.landArea ?? null,
      landAreaUnit: property.landAreaUnit ?? null,
      yearBuilt: property.yearBuilt ?? null,
      condition: property.condition ?? null,
      primaryImageUrl: property.heroMedia?.url || property.primaryImageUrl || null,
      currency: property.currency,
    },
    
    location: location ? {
      name: location.name ?? null,
      city: location.city || location.localityName || null,
      district: location.district || location.administrativeAreaName || null,
      neighbourhood: location.neighbourhood ?? null,
      region: location.region ?? null,
      country: location.country || location.countryName || null,
      suburb: location.suburb ?? null,
      addressLine1: location.addressLine1 ?? null,
      latitude: location.latitude ?? null,
      longitude: location.longitude ?? null,
    } : null,
    
    listingType: listing.listingType,
    status: listing.status,
    displayPriceText: listing.displayPriceText ?? null,
    mainHeadline: listing.mainHeadline ?? null,
    
    price,
    rentalTerms: rentalTerms ? {
      minLeaseMonths: rentalTerms.minLeaseMonths ?? null,
      maxLeaseMonths: rentalTerms.maxLeaseMonths ?? null,
      furnishedStatus: rentalTerms.furnishedStatus ?? null,
      petPolicy: rentalTerms.petPolicy ?? null,
      depositAmount: rentalTerms.depositAmount ?? null,
      depositCurrency: rentalTerms.depositCurrency ?? null,
    } : null,
    auctionDetails,
    listingAgent,
    
    listedDate: listing.listedDate ?? null,
    availableDate: listing.availableDate ?? null,
    
    propertyType: property.propertyType ? {
      id: property.propertyType.id,
      name: property.propertyType.name,
      icon: property.propertyType.icon ?? null,
    } : null,
    
    development: extractDevelopmentInfo(listing),
  };
}

/**
 * Extract development info from a listing.
 * A property may belong to a development via property.development (preferred)
 * or via managedAsset.development.
 */
function extractDevelopmentInfo(listing: Listing): ListingCardData['development'] {
  const propertyDev = listing.managedAsset?.property?.development;
  if (propertyDev) {
    return {
      id: propertyDev.id,
      name: propertyDev.name,
      slug: propertyDev.slug ?? null,
    };
  }
  
  const assetDev = listing.managedAsset?.development;
  if (assetDev) {
    return {
      id: assetDev.id,
      name: assetDev.name,
      slug: assetDev.slug ?? null,
    };
  }
  
  return null;
}

/**
 * Extract the primary price from a listing based on its type
 */
function extractPrimaryPrice(listing: Listing): ListingCardData['price'] {
  // Sale listing
  if (listing.saleDetails?.primaryPrice) {
    const p = listing.saleDetails.primaryPrice;
    return {
      amount: p.amount,
      currency: p.currency,
      priceType: String(p.priceType),
      frequency: null,
      isTaxInclusive: p.isTaxInclusive ?? null,
    };
  }
  
  // Rental listing
  if (listing.rentalDetails?.primaryPrice) {
    const p = listing.rentalDetails.primaryPrice;
    return {
      amount: p.amount,
      currency: p.currency,
      priceType: String(p.priceType),
      frequency: p.frequency ? String(p.frequency) : null,
      isTaxInclusive: p.isTaxInclusive ?? null,
    };
  }
  
  // Auction listing - use guide price
  if (listing.auctionDetails?.guidePriceMin) {
    const p = listing.auctionDetails.guidePriceMin;
    return {
      amount: p.amount,
      currency: p.currency,
      priceType: 'GUIDE',
      frequency: null,
      isTaxInclusive: null,
    };
  }
  
  return null;
}

/**
 * Get display label for listing type
 */
export function getListingTypeLabel(type: ListingType): string {
  const labels: Record<ListingType, string> = {
    SALE: 'For Sale',
    RENTAL: 'For Rent',
    AUCTION: 'Auction',
    TENDER: 'Tender',
    EXPRESSION_OF_INTEREST: 'EOI',
    LEASE: 'Lease',
    COMMERCIAL_SALE: 'Commercial Sale',
    COMMERCIAL_RENTAL: 'Commercial Rent',
    COMMERCIAL_LEASE: 'Commercial Lease',
    HOLIDAY_RENTAL: 'Holiday Rental',
    ROOM_RENTAL: 'Room Rental',
    SUBLET: 'Sublet',
  };
  return labels[type] || type;
}

/**
 * Get display label for listing status
 */
export function getListingStatusLabel(status: ListingStatus): string {
  const labels: Record<ListingStatus, string> = {
    DRAFT: 'Draft',
    ACTIVE: 'Active',
    COMING_SOON: 'Coming Soon',
    UNDER_OFFER: 'Under Offer',
    SOLD: 'Sold',
    LEASED: 'Leased',
    WITHDRAWN: 'Withdrawn',
    ARCHIVED: 'Archived',
    EXPIRED: 'Expired',
  };
  return labels[status] || status;
}

/**
 * Get CSS color classes for listing status
 */
export function getListingStatusColor(status: ListingStatus): string {
  const colors: Record<ListingStatus, string> = {
    DRAFT: 'bg-gray-500 text-white',
    ACTIVE: 'bg-green-500 text-white',
    COMING_SOON: 'bg-blue-500 text-white',
    UNDER_OFFER: 'bg-amber-500 text-white',
    SOLD: 'bg-red-600 text-white',
    LEASED: 'bg-purple-600 text-white',
    WITHDRAWN: 'bg-gray-600 text-white',
    ARCHIVED: 'bg-gray-400 text-white',
    EXPIRED: 'bg-gray-500 text-white',
  };
  return colors[status] || 'bg-gray-500 text-white';
}

/**
 * Get CSS color classes for listing type
 */
export function getListingTypeColor(type: ListingType): string {
  if (type === 'SALE' || type === 'COMMERCIAL_SALE') {
    return 'bg-emerald-600 text-white';
  }
  if (type === 'RENTAL' || type === 'COMMERCIAL_RENTAL' || type === 'HOLIDAY_RENTAL' || type === 'ROOM_RENTAL') {
    return 'bg-blue-600 text-white';
  }
  if (type === 'AUCTION') {
    return 'bg-amber-600 text-white';
  }
  if (type === 'LEASE' || type === 'COMMERCIAL_LEASE') {
    return 'bg-indigo-600 text-white';
  }
  return 'bg-gray-600 text-white';
}

/**
 * Format price for display
 */
export function formatListingPrice(
  price: ListingCardData['price'],
  locale: string = 'en'
): string {
  if (!price) return 'Price on request';
  
  const formatted = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: price.currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price.amount);
  
  // Add frequency for rentals
  if (price.frequency) {
    const frequencyLabels: Record<string, string> = {
      HOURLY: '/hour',
      DAILY: '/day',
      WEEKLY: '/week',
      FORTNIGHTLY: '/fortnight',
      MONTHLY: '/month',
      QUARTERLY: '/quarter',
      ANNUALLY: '/year',
      ONE_TIME: '',
    };
    return `${formatted}${frequencyLabels[price.frequency] || ''}`;
  }
  
  return formatted;
}

/**
 * Format auction guide price range
 */
export function formatAuctionGuidePrice(
  auctionDetails: ListingCardData['auctionDetails'],
  locale: string = 'en'
): string {
  if (!auctionDetails) return 'Guide price TBA';
  
  const currency = auctionDetails.currency || DEFAULT_CURRENCY;
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  
  if (auctionDetails.guidePriceMin && auctionDetails.guidePriceMax) {
    return `${formatter.format(auctionDetails.guidePriceMin)} - ${formatter.format(auctionDetails.guidePriceMax)}`;
  }
  
  if (auctionDetails.guidePriceMin) {
    return `From ${formatter.format(auctionDetails.guidePriceMin)}`;
  }
  
  if (auctionDetails.guidePriceMax) {
    return `Up to ${formatter.format(auctionDetails.guidePriceMax)}`;
  }
  
  return 'Guide price TBA';
}
